/*
 * How the core reaches ai-router (`/api/chat`, `/api/sessions`, `/api/media/*`,
 * `/api/attachments`, `/api/theme`).
 *
 * In the panel this is the browser's `fetch`, and nothing here changes that: with no override
 * installed, `aiRouterFetch` is the global `fetch`, read at call time so a test that stubs
 * `globalThis.fetch` still intercepts every call.
 *
 * ★ INSIDE ai-router ITSELF THERE IS NO SELF-FETCH. A Worker cannot request its own route
 *   (Cloudflare refuses the loop), and the headless runner (HUBBY-HEADLESS-ACTIONS-PLAN.md S3b)
 *   runs this code inside ai-router. So the worker installs an override that dispatches the
 *   request to its own handler in-process — same URL, same headers, same body, same
 *   authentication — and the six modules that call ai-router need no other change.
 *
 * The override is a single module-level function. A host that serves concurrent runs must
 * make it read per-run state itself (ai-router keeps the run in an AsyncLocalStorage store);
 * installing a different function per run would race.
 */
export type AiRouterFetch = (url: string, init?: RequestInit) => Promise<Response>;

let override: AiRouterFetch | null = null;

/** Install (or with null, remove) the host's way of reaching ai-router. */
export function setAiRouterFetch(impl: AiRouterFetch | null): void {
    override = impl;
}

export function aiRouterFetch(url: string, init?: RequestInit): Promise<Response> {
    return override ? override(url, init) : fetch(url, init);
}

/*
 * WHICH ai-router. There are two deployments (Cloudflare/ai-router/wrangler.jsonc): production at
 * ai-router.hd.pics and `--env test` at ai-router-test.hd.pics, each with its own D1 and attachments
 * bucket. Each accepts only its own environment's API host (PANEL_ORIGINS), so the choice follows
 * the API host the panel is talking to, not a dev flag: test01 and the dev boxes are served by
 * api-dev.hdphotohub.workers.dev/{devHost}, production by api.hd.pics.
 *
 * The core cannot see the API host (rule 3 in README.md), so the host installs a resolver read at
 * call time. The panel's is in `services/http.ts`; the evals install their own, the test worker
 * (`evals/hubby-analytics/agentic-loop.ts`, ROBO-881). With none installed — ai-router itself and
 * the MCP worker — it is production, as it always was. Inside ai-router the URL is
 * cosmetic anyway: `aiRouterFetch` above dispatches in-process whatever host it names.
 */
export const AI_ROUTER_PRODUCTION_URL = 'https://ai-router.hd.pics';
export const AI_ROUTER_TEST_URL = 'https://ai-router-test.hd.pics';
const TEST_API_HOSTNAME = 'api-dev.hdphotohub.workers.dev';

/** The ai-router that accepts calls naming `apiHost` (scheme-less, may carry a path). */
export function aiRouterUrlForApiHost(apiHost: string | null | undefined): string {
    const hostname = (apiHost ?? '').trim().toLowerCase().split('/')[0];
    return hostname === TEST_API_HOSTNAME ? AI_ROUTER_TEST_URL : AI_ROUTER_PRODUCTION_URL;
}

let baseUrlResolver: (() => string) | null = null;

/** Install (or with null, remove) the host's way of choosing the ai-router base URL. */
export function setAiRouterBaseUrl(resolve: (() => string) | null): void {
    baseUrlResolver = resolve;
}

/** The ai-router base URL for this call, without a trailing slash. */
export function aiRouterBaseUrl(): string {
    return (baseUrlResolver?.() || AI_ROUTER_PRODUCTION_URL).replace(/\/$/, '');
}
